import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	ICmmsRequestsQueryVariables,
	IRequest,
} from '@app/pages/cmms/interfaces/request.interfaces';
import {
	addRequest,
	addRequestExternalData,
	addRequests,
	clearRequestExternalData,
	deleteRequest,
	deleteRequests,
	dropRequestsQueryVariables,
	mapRequest,
	mapRequests,
	openNewRequestFromOutside,
	requestsLoaded,
	setRequest,
	setRequestForEditing,
	setRequests,
	setRequestsTableConfig,
	startLoadingRequests,
	storeRequestsQueryVariables,
	updateRequest,
	updateRequests,
	upsertRequest,
	upsertRequests,
} from '@app/pages/cmms/components/request/state/requests.action';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { DocumentNode } from 'graphql/index';
import { RequestsQuery } from '@app/pages/cmms/graphql/requests.queries';

export interface IRequestState extends EntityState<IRequest> {
	// additional entity state properties
	requestPagination: IDjangoPaginationData | null;
	requestAuPagination: IAuPaginationData | null;
	requestsQueryVariables: ICmmsRequestsQueryVariables | null;
	requestEdit: IRequest;
	isLoadingRequests: boolean;
	isExportable: boolean;
	tableColumnsConfig: ICMMSColumn[] | null;
	exportUrlQuery: DocumentNode | null;
	requestData?: Partial<IRequest>;
}

export function selectRequestId(item: IRequest): string {
	return item.pk;
}

export const RequestAdapter: EntityAdapter<IRequest> = createEntityAdapter<IRequest>({
	selectId: selectRequestId,
});

export const initialState: IRequestState = RequestAdapter.getInitialState({
	// additional entity state properties
	requestPagination: null,
	requestEdit: null,
	isLoadingRequests: true,
	requestAuPagination: null,
	requestsQueryVariables: null,
	isExportable: false,
	tableColumnsConfig: null,
	exportUrlQuery: structuredClone(RequestsQuery),
});

export const requestReducer = createReducer(
	initialState,
	on(addRequest, (state, { request }) => {
		return RequestAdapter.addOne(request, state);
	}),
	on(setRequest, (state, { request }) => {
		return RequestAdapter.setOne(request, state);
	}),
	on(upsertRequest, (state, { request }) => {
		return RequestAdapter.upsertOne(request, state);
	}),
	on(addRequests, (state, { requests }) => {
		return RequestAdapter.addMany(requests, state);
	}),
	on(upsertRequests, (state, { requests }) => {
		return RequestAdapter.upsertMany(requests, state);
	}),
	on(updateRequest, (state, { update }) => {
		return RequestAdapter.updateOne(update, {
			...state,
			requestEdit: update.changes as IRequest,
		});
	}),
	on(updateRequests, (state, { updates }) => {
		return RequestAdapter.updateMany(updates, state);
	}),
	on(mapRequest, (state, { entityMap }) => {
		return RequestAdapter.mapOne(entityMap, state);
	}),
	on(mapRequests, (state, { entityMap }) => {
		return RequestAdapter.map(entityMap, state);
	}),
	on(deleteRequest, (state, { pk }) => {
		return RequestAdapter.removeOne(pk, state);
	}),
	on(deleteRequests, (state, { pks }) => {
		return RequestAdapter.removeMany(pks, state);
	}),
	on(startLoadingRequests, (state, { variables }) => {
		return {
			...state,
			isLoadingRequests: true,
			requestAuPagination: {
				first: variables?.first,
				last: variables?.last,
				after: variables?.after,
				before: variables?.before,
			},
		};
	}),
	on(requestsLoaded, (state, { requests, requestPagination }) => {
		return RequestAdapter.setAll(requests, {
			...state,
			requestPagination: requestPagination,
			isLoadingRequests: false,
		});
	}),
	on(setRequestForEditing, (state, { requestEdit }) => {
		return { ...state, requestEdit };
	}),
	on(setRequests, (state, { requests }) => {
		return RequestAdapter.setMany(requests, state);
	}),
	on(storeRequestsQueryVariables, (state, { variables }) => {
		return { ...state, requestsQueryVariables: variables };
	}),
	on(dropRequestsQueryVariables, state => {
		return { ...state, requestsQueryVariables: null };
	}),
	on(setRequestsTableConfig, (state, { isExportable, columns }) => {
		return {
			...state,
			isExportable,
			tableColumnsConfig: columns,
		};
	}),
	on(addRequestExternalData, (state, { requestData }) => {
		return {
			...state,
			requestData,
		};
	}),
	on(clearRequestExternalData, state => {
		return {
			...state,
			requestData: null,
		};
	}),
	on(openNewRequestFromOutside, (state, { requestData }) => {
		return {
			...state,
			requestData,
		};
	})
);

const { selectIds, selectEntities, selectAll, selectTotal } =
	RequestAdapter.getSelectors();

export const RequestEntitySelector = {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
};
